import { ErrorMessage, Field, Form as FormikForm, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { FaPlus } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import axios from '../../admin-axios';
import { ReactComponent as SuccessIcon } from '../../assets/Images/success_icon.svg';
import useActions from '../../hooks/useActions';
import { detectError } from '../../utils';
import LongPaginationHandler from '../LongPaginationHandler';
import classes from './index.module.scss';
import SearchTableRow from './SearchTableRow';

const initialValues = {
  firstname: '',
  lastname: '',
  phonenumber: '',
  designation_id: '',
  kin_firstname: '',
  kin_lastname: '',
  kin_relationship: '',
  kin_phonenumber: ''
};

const phoneRules =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const validationSchema = Yup.object({
  firstname: Yup.string().required('Required'),
  lastname: Yup.string().required('Required'),
  phonenumber: Yup.string()
    .min(11, 'phone number should be 11 chars')
    .max(11, 'Phone number should be 11 chars')
    .matches(phoneRules, { message: 'please enter a valid phone number' })
    .required(' Phone number is required'),
  designation_id: Yup.string().required('Required'),
  kin_firstname: Yup.string().required('kins lastname is required'),
  kin_lastname: Yup.string().required('kins lastname is required'),
  kin_relationship: Yup.string().required('relationship is required'),
  kin_phonenumber: Yup.string()
    .min(11, 'phone number should be 11 chars')
    .max(11, 'Phone number should be 11 chars')
    .matches(phoneRules, { message: 'please enter a valid phone number' })
    .required('kins phone number is required')
});

const StaffTable = ({ match }) => {
  const [formLoading, setFormLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const searchRef = useRef();
  // const [error] = useState(false);

  const {
    staffs,
    loading,
    total,
    page,
    perPage,
    searchResults,
    searchResultsTotal,
    searchResultsPage,
    searchResultsLoading,
    searchResultsPerPage,
    staffView
  } = useSelector((state) => state.staffs);

  const { getStaffs, setStaffView, searchStaffs } = useActions();

  const {
    user: { stationId, id }
  } = useSelector((state) => state.auth);

  const { designation } = useSelector((state) => state.designations);

  const handleFormClose = () => setShowForm(false);
  const handleFormShow = () => setShowForm(true);

  const paginate = (pageNumber) => {
    // setPage(pageNumber);
    getStaffs(stationId, pageNumber);
  };

  const paginateSearch = (pageNumber) => {
    // setPage(pageNumber);
    searchStaffs(stationId, searchRef.current.value, pageNumber);
  };

  const searchHandler = (event) => {
    if (event.code === 'Enter') {
      setStaffView('searching');
      searchStaffs(stationId, searchRef.current.value, page);
      searchRef.current.value = '';
    }
  };

  useEffect(() => {
    if (staffView === 'normal') {
      if (!staffs) {
        getStaffs(stationId, page);
      }
    }
  }, [page, stationId, staffView, staffs, getStaffs]);

  useEffect(() => {
    document.body.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <div className={classes.staff_table}>
        <div className="row g-3 mb-5">
          <div className="col-sm-6 col-md-7 col-lg-9">
            <div className={classes.staff_table_search}>
              <input
                type="text"
                className={`form-control p-2 ${classes.staff_table_search_input}`}
                placeholder="Search by Staff name, Designation"
                aria-label="search"
                aria-describedby="button-addon2"
                ref={searchRef}
                onKeyDown={searchHandler}
              />
            </div>
          </div>
          <div className="col-sm-6 col-md-5 col-lg-3">
            <button
              onClick={handleFormShow}
              type="button"
              className={`btn ${classes.staff_table_search_button}`}>
              <FaPlus /> Register Staff
            </button>
          </div>
        </div>
        {staffView === 'searching' && (
          <div className="">
            <button
              onClick={() => setStaffView('normal')}
              type="button"
              className={`mb-3 btn ${classes.staff_table_general_button}`}>
              Back to All Staffs
            </button>
          </div>
        )}
        <div className={classes.staff_table_wrapper}>
          <table className={`table table-striped ${classes.staff_table_table}`}>
            <thead>
              <tr>
                <th scope="col">Staff Name</th>
                <th scope="col">Designation</th>
                <th scope="col">Status</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            {staffView === 'searching' && (
              <SearchTableRow
                staffs={searchResults}
                loading={searchResultsLoading}
                classes={classes}
                path={match.url}
              />
            )}

            {staffView === 'normal' && (
              <SearchTableRow
                staffs={staffs}
                loading={loading}
                classes={classes}
                path={match.url}
              />
            )}
          </table>
        </div>
        {staffView === 'normal' && (
          <LongPaginationHandler
            elementsPerPage={perPage}
            totalElements={total}
            activePage={page}
            paginate={paginate}
          />
        )}

        {staffView === 'searching' && (
          <LongPaginationHandler
            elementsPerPage={searchResultsPerPage}
            totalElements={searchResultsTotal}
            activePage={searchResultsPage}
            paginate={paginateSearch}
          />
        )}
      </div>
      <div className={classes.staff_modals}>
        <Modal show={showForm} onHide={handleFormClose}>
          <Modal.Header closeButton>
            <Modal.Title className={classes.staff_modals_form_title}>
              <span>Register Staff</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={async (values) => {
                try {
                  setFormLoading(true);

                  // Register new staff member
                  const response = await axios.post(`/staff/register/${stationId}`, {
                    firstname: values.firstname,
                    lastname: values.lastname,
                    phone: values.phonenumber,
                    designation_id: values.designation_id,
                    kin_firstname: values.kin_firstname,
                    kin_lastname: values.kin_lastname,
                    kin_phone: values.kin_phonenumber,
                    kin_relationship: values.kin_relationship,
                    created_by: id,
                    station_id: stationId
                  });

                  if (response.data.message === 'Staff created') {
                    setFormLoading(false);
                    setShowSuccess(true);
                    handleFormClose();
                    getStaffs(stationId, 1);
                  } else {
                    toast.error('Staff was not created successfully. Check your inputs.');
                    setFormLoading(false);
                  }
                } catch (error) {
                  setFormLoading(false);
                  toast.error(detectError(error));
                }
              }}>
              {(formik) => {
                return (
                  <FormikForm className="mb-5">
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="firstname" className="fw-bold">
                        First Name
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="First Name"
                        name="firstname"
                        id="firstname"
                      />
                      <ErrorMessage component="div" className="text-danger p-0" name="firstname" />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="lastname" className="fw-bold">
                        Last Name
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="Last Name"
                        name="lastname"
                        id="lastname"
                      />
                      <ErrorMessage component="div" className="text-danger p-0" name="lastname" />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="phonenumber" className="fw-bold">
                        Phone Number
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="Phone Number"
                        name="phonenumber"
                        id="phonenumber"
                      />
                      <ErrorMessage
                        component="div"
                        className="text-danger p-0"
                        name="phonenumber"
                      />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="fw-bold">Designation</label>
                      <Field as="select" className="form-select" name="designation_id">
                        <option value="">Options</option>
                        {designation ? (
                          designation.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          ))
                        ) : (
                          <option key={999} value="">
                            Designation not available
                          </option>
                        )}
                      </Field>
                      <ErrorMessage
                        component="div"
                        className="text-danger p-0"
                        name="designation_id"
                      />
                    </div>

                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="kin_firstname" className="fw-bold">
                        kin's First Name
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="Next of Kin's First Name"
                        name="kin_firstname"
                        id="kin_firstname"
                      />
                      <ErrorMessage
                        component="div"
                        className="text-danger p-0"
                        name="kin_firstname"
                      />
                    </div>

                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="kin_lastname" className="fw-bold">
                        Kins's Last Name
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="Next of Kin's Last Name"
                        name="kin_lastname"
                        id="kin_lastname"
                      />
                      <ErrorMessage
                        component="div"
                        className="text-danger p-0"
                        name="kin_lastname"
                      />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="fw-bold">Relationship</label>
                      <Field as="select" className="form-select" name="kin_relationship">
                        <option value="">Options</option>
                        <option value="father">Father</option>
                        <option value="mother">Mother</option>
                        <option value="sibling">Sibling</option>
                        <option value="cousin">Cousin</option>
                      </Field>
                      <ErrorMessage
                        component="div"
                        className="text-danger p-0"
                        name="kin_relationship"
                      />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="kin_phonenumber" className="fw-bold">
                        Kin's Phone Number
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="Next Of Kin's Phone Number"
                        name="kin_phonenumber"
                        id="kin_phonenumber"
                      />
                      <ErrorMessage
                        component="div"
                        className="text-danger p-0"
                        name="kin_phonenumber"
                      />
                    </div>
                    <div className="mb-2">
                      {/* {error && <p>{error}</p>} */}
                      {formLoading ? (
                        <div className="text-center d-flex justify-content-center w-100 h-100">
                          <Spinner animation="border" variant="dark" />
                        </div>
                      ) : (
                        <Button
                          disabled={!formik.isValid}
                          className={`w-100 ${classes.staff_modals_form_button}`}
                          variant="primary"
                          type="submit">
                          Register Staff
                        </Button>
                      )}
                    </div>
                  </FormikForm>
                );
              }}
            </Formik>
          </Modal.Body>
        </Modal>
        <Modal
          size="md"
          show={showSuccess}
          onHide={() => setShowSuccess(false)}
          centered
          aria-labelledby="example-modal-sizes-title-sm">
          <Modal.Body>
            <div className="d-flex flex-column justify-content-center align-items-center p-4">
              <SuccessIcon />
              <div className="mt-4 fs-5 text-center fw-bold">
                Yaay!! You've successfully registered a new staff.
              </div>
              <Button
                className={`mt-5 ${classes.staff_modals_form_button}`}
                variant="primary"
                onClick={() => setShowSuccess(false)}>
                Okay
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};
export default StaffTable;
